export type DocumentKeyType = 'invoice' | 'check' | 'document';

export interface DocumentKeyParts {
  folio: string;
  businessIdentifier: string;
  stakeholderIdentifier?: string;
}

const PREFIXES: Record<DocumentKeyType, string> = {
  invoice: 'invoice:',
  check: 'check:',
  document: 'document:',
};

function buildTwoPartKey(folio: string, businessIdentifier: string): string {
  return `${folio}-${businessIdentifier}`;
}

function parseTwoPartKey(payload: string): DocumentKeyParts | null {
  const segments = payload.split('-');
  if (segments.length < 2) return null;
  const [folio, ...rest] = segments;
  if (rest.length === 0) return null;
  const businessIdentifier =
    rest.length >= 2 ? `${rest[0]}-${rest[1]}` : rest[0];
  return { folio, businessIdentifier };
}

function buildCheckKey(parts: DocumentKeyParts): string {
  const { folio, businessIdentifier, stakeholderIdentifier } = parts;
  return `${folio}|${businessIdentifier}|${stakeholderIdentifier ?? ''}`;
}

function parseCheckKey(payload: string): DocumentKeyParts | null {
  const parts = payload.split('|');
  if (parts.length < 3) return null;
  return {
    folio: parts[0] ?? '',
    businessIdentifier: parts[1] ?? '',
    stakeholderIdentifier: parts[2] ?? undefined,
  };
}

export function buildDocumentKey(
  type: DocumentKeyType,
  doc: DocumentKeyParts,
): string {
  const payload =
    type === 'check'
      ? buildCheckKey(doc)
      : buildTwoPartKey(doc.folio, doc.businessIdentifier);
  return `${PREFIXES[type]}${payload}`;
}

export interface ParsedDocumentKey extends DocumentKeyParts {
  type: DocumentKeyType;
}

export function parseDocumentKey(key: string): ParsedDocumentKey | null {
  const trimmed = key?.trim();
  if (!trimmed) return null;

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex > 0) {
    const prefix = trimmed.slice(0, colonIndex) as DocumentKeyType;
    const payload = trimmed.slice(colonIndex + 1);
    if (prefix === 'check') {
      const parts = parseCheckKey(payload);
      return parts ? { type: 'check', ...parts } : null;
    }
    if (prefix === 'invoice' || prefix === 'document') {
      const parts = parseTwoPartKey(payload);
      return parts ? { type: prefix, ...parts } : null;
    }
    return null;
  }

  if (trimmed.includes('|')) {
    const parts = parseCheckKey(trimmed);
    return parts ? { type: 'check', ...parts } : null;
  }
  const parts = parseTwoPartKey(trimmed);
  return parts ? { type: 'document', ...parts } : null;
}

function uniqueKeys(keys: string[]): string[] {
  return Array.from(
    new Set(keys.map((k) => k.trim()).filter((k) => Boolean(k) && k !== '-')),
  );
}

export function normalizePriorityKeys(keys: string[]): string[] {
  return uniqueKeys(
    keys.map((key) => {
      const parsed = parseDocumentKey(key);
      if (!parsed?.folio || !parsed.businessIdentifier) return '';
      return buildDocumentKey(parsed.type, parsed);
    }),
  );
}

export function getDocumentKeyType(
  subProductSelected: string | undefined,
): DocumentKeyType {
  if (subProductSelected === 'check') return 'check';
  if (subProductSelected === 'invoice') return 'invoice';
  return 'document';
}

export function mergePriorityKeys(
  previous: string[],
  next: string[],
): string[] {
  return uniqueKeys([...previous, ...next]);
}
